// @ts-expect-error - No types available for joplin-turndown-plugin-gfm
import { gfm } from 'joplin-turndown-plugin-gfm';
import TurndownService from 'turndown';

const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-', // Optional customization for unordered lists
  codeBlockStyle: 'fenced',
  hr: '---',
});

turndownService.use(gfm);

// Add a custom rule for tables to properly handle th elements
turndownService.addRule('tables', {
  filter: ['table'],
  replacement(tableContent, node) {
    const tableElement = node as HTMLTableElement;

    // Don't convert empty tables
    if (!tableElement.rows || tableElement.rows.length === 0) {
      return '';
    }

    const rows = Array.from(tableElement.rows);
    const markdownRows: string[] = [];

    // First pass: determine column count and find header cells
    const hasExplicitHeaders = rows.some((row) =>
      Array.from(row.cells).some((cell) => cell.tagName.toLowerCase() === 'th'),
    );

    // Calculate max column count across all rows for consistency
    const columnCount = rows.reduce(
      (max, row) => Math.max(max, row.cells.length),
      0,
    );

    // First check if there's a thead element with rows
    const theadElement = tableElement.querySelector('thead');
    if (theadElement && theadElement.rows.length > 0) {
      // Use the thead row as header
      const headerRow = theadElement.rows[0];
      if (!headerRow) {
        return '';
      }
      const headerCells = Array.from(headerRow.cells);

      const headerValues = headerCells.map((cell) => {
        const cellContent = turndownService.turndown(cell.innerHTML).trim();
        return cellContent || ' ';
      });

      // Ensure header has enough columns
      while (headerValues.length < columnCount) {
        headerValues.push(' ');
      }

      markdownRows.push(`| ${headerValues.join(' | ')} |`);
    } else if (hasExplicitHeaders) {
      // Find the first row with th elements
      const headerRow = rows.find((row) =>
        Array.from(row.cells).some(
          (cell) => cell.tagName.toLowerCase() === 'th',
        ),
      );

      if (headerRow) {
        const cells = Array.from(headerRow.cells);
        const headerValues = cells.map((cell) => {
          const cellContent = turndownService.turndown(cell.innerHTML).trim();
          return cellContent || ' ';
        });

        // Ensure header has enough columns
        while (headerValues.length < columnCount) {
          headerValues.push(' ');
        }

        markdownRows.push(`| ${headerValues.join(' | ')} |`);
      }
    } else if (rows.length > 0) {
      // Fallback: if no header row found, create an empty header based on column count
      markdownRows.push(`| ${Array(columnCount).fill(' ').join(' | ')} |`);
    }

    // Add separator row
    markdownRows.push(`| ${Array(columnCount).fill('---').join(' | ')} |`);

    // Process content rows, skipping any rows that were used as headers
    rows
      .filter((row) => {
        // Skip rows that were already processed as headers
        if (theadElement && theadElement.contains(row)) {
          return false;
        }

        // Skip rows with th elements that we already processed as header
        if (
          hasExplicitHeaders &&
          Array.from(row.cells).some(
            (cell) => cell.tagName.toLowerCase() === 'th',
          )
        ) {
          return false;
        }

        return true;
      })
      .forEach((row) => {
        const cells = Array.from(row.cells);
        const values = cells.map((cell) => {
          const cellContent = turndownService.turndown(cell.innerHTML).trim();
          return cellContent || ' ';
        });

        // Pad with empty cells if needed
        while (values.length < columnCount) {
          values.push(' ');
        }

        markdownRows.push(`| ${values.join(' | ')} |`);
      });

    return markdownRows.join('\n');
  },
});

// Add a rule to clean up list items
turndownService.addRule('preserveLists', {
  filter: ['ul', 'ol'],
  replacement: (content: string, node: Node): string => {
    if (
      !(
        node.nodeType === 1 &&
        typeof (node as HTMLElement).nodeName === 'string'
      )
    ) {
      return '';
    }
    const htmlNode = node as HTMLElement;

    const processList = (listNode: HTMLElement, level = 0): string => {
      // Recalculate the list type for the current node
      const listType = listNode.nodeName.toLowerCase();
      return Array.from(listNode.children)
        .map((child, index) => {
          if (child.nodeName.toLowerCase() === 'li') {
            const prefix = listType === 'ol' ? `${index + 1}. ` : '- ';

            // Clone the <li> so we can remove any nested lists from its content.
            const liClone = child.cloneNode(true) as HTMLElement;
            const nestedList = liClone.querySelector('ul, ol');
            if (nestedList) {
              nestedList.remove();
            }
            // Convert the remaining inner HTML to Markdown to preserve inline formatting
            const listItemMarkdown = turndownService
              .turndown(liClone.innerHTML)
              .trim();

            // Process any nested list elements from the original <li>
            const subListElement = Array.from(child.children).find(
              (nestedNode) => {
                const nodeName = nestedNode.nodeName.toLowerCase();
                return nodeName === 'ul' || nodeName === 'ol';
              },
            ) as HTMLElement | undefined;

            const subListContent = subListElement
              ? `\n${processList(subListElement, level + 1)}`
              : '';

            // Use two spaces per level to indent nested list items
            return `${'  '.repeat(level)}${prefix}${listItemMarkdown}${subListContent}`;
          }
          return '';
        })
        .join('\n');
    };

    return `\n${processList(htmlNode)}\n`;
  },
});

// Custom rule for code blocks
turndownService.addRule('codeBlock', {
  filter: (node) => {
    return (
      node.nodeName.toLowerCase() === 'pre' &&
      node.firstChild?.nodeName.toLowerCase() === 'code'
    );
  },
  replacement: (content, node) => {
    const codeNode = node.firstChild;
    const codeContent = codeNode?.textContent || '';
    return `\n\`\`\`\n${codeContent.trim()}\n\`\`\`\n`;
  },
});

turndownService.addRule('strikethrough', {
  filter: ['s'], // Match HTML tags for strikethrough
  replacement: (content) => {
    return `~~${content}~~`; // Wrap the content in double tildes
  },
});

turndownService.addRule('underline', {
  filter: ['u'], // Match the <u> tag
  replacement: (content) => {
    return `<u>${content}</u>`; // Wrap content in inline HTML for underline
  },
});

turndownService.addRule('superscript', {
  filter: ['sup'], // Match <sup> tags
  replacement: (content) => {
    return `<sup>${content}</sup>`; // Wrap content in <sup>
  },
});

// Add a rule for subscript
turndownService.addRule('subscript', {
  filter: ['sub'], // Match <sub> tags
  replacement: (content) => {
    return `<sub>${content}</sub>`; // Wrap content in <sub>
  },
});

turndownService.addRule('horizontalRule', {
  filter: ['hr'],
  replacement: () => {
    return '---'; // Replace <hr> with ---
  },
});

// Add a custom rule for italic text
turndownService.addRule('italic', {
  filter: ['em', 'i'], // Match <em> and <i> tags
  replacement: (content) => {
    return `*${content}*`; // Wrap the content in asterisks
  },
});

turndownService.addRule('bold', {
  filter: ['strong', 'b'], // Match <strong> and <b> tags
  replacement: (content) => {
    return `**${content}**`; // Wrap the content in double asterisks
  },
});

// Custom rule for <a> tags to collapse extra whitespace and replace removed content with dot markers
turndownService.addRule('anchor', {
  filter: 'a',
  replacement: (content, node) => {
    // Check that node is an element and has a getAttribute function
    if (
      node.nodeType !== 1 ||
      typeof (node as Element).getAttribute !== 'function'
    ) {
      return content;
    }
    const element = node as Element;
    const href = element.getAttribute('href');
    if (!href) return content;

    // Collapse extra whitespace and newlines in the content
    const cleanedContent = content.replace(/\s+/g, ' ').trim();

    return `[${cleanedContent}](${href})`;
  },
});

// Convert task list items to markdown checkbox syntax.
// Must be added last so it takes precedence over gfm and preserveLists rules.
turndownService.addRule('taskList', {
  filter: (node) => {
    return (
      node.nodeName.toLowerCase() === 'ul' &&
      (node as HTMLElement).getAttribute('data-type') === 'taskList'
    );
  },
  replacement: (_content, node) => {
    const children = Array.from((node as HTMLElement).children);
    const lines = children
      .filter((child) => child.nodeName.toLowerCase() === 'li')
      .map((li) => {
        const isChecked = li.getAttribute('data-checked') === 'true';
        const checkbox = isChecked ? '[x]' : '[ ]';

        // Clone the li and remove the label (checkbox UI) to get just the text content
        const clone = li.cloneNode(true) as HTMLElement;
        const label = clone.querySelector('label');
        if (label) label.remove();

        const content = turndownService.turndown(clone.innerHTML).trim();
        return `- ${checkbox} ${content}`;
      });
    return `\n${lines.join('\n')}\n`;
  },
});

export const htmlToMarkdown = (html: string): string => {
  return turndownService.turndown(html);
};
